import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Calculator, User as UserIcon, Sparkles, FileBarChart, Receipt, Calendar, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";

const SUGGESTIONS = [
  { icon: Calendar,      txt: "Quali sono le prossime scadenze fiscali del mese?" },
  { icon: Receipt,       txt: "Analizza l'IVA del mese e calcola la liquidazione trimestrale" },
  { icon: TrendingUp,    txt: "Come posso ottimizzare la deducibilità dei costi sul noleggio?" },
  { icon: FileBarChart,  txt: "Riepilogo bilancio semplificato dei dati attuali" },
  { icon: Calculator,    txt: "Sto rispettando la regola dell'art. 164 TUIR sui veicoli aziendali?" },
];

export default function Commercialista() {
  const [sessionId] = useState(() => `comm-${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ctxPreview, setCtxPreview] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    api.get("/ai/business-context").then((r) => setCtxPreview(r.data.context || "")).catch(() => {});
  }, []);

  const send = async (text) => {
    const message = text || input.trim();
    if (!message) return;
    setMessages((m) => [...m, { role: "user", content: message }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ai/commercialista", { session_id: sessionId, message });
      setMessages((m) => [...m, { role: "assistant", content: r.data.response }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Errore: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="h-[calc(100vh-9rem)] flex flex-col" data-testid="commercialista-page">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Calculator className="text-emerald-400" size={28} />
            <span className="text-emerald-400">Commercialista</span>
            <span className="text-xs uppercase tracking-[0.3em] text-white/40 font-display">Virtuale AI</span>
          </h1>
          <p className="text-sm text-white/45 mt-1">Esperto fisco italiano · TUIR · IVA · FatturaPA SDI · liquidazioni · scadenze · ottimizzazione tributaria</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-400">
          <Sparkles size={14} /> Live business data
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 overflow-hidden">
        <div className="glass rounded-2xl overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto p-6 space-y-4" data-testid="comm-messages">
            {messages.length === 0 && (
              <div className="h-full grid place-items-center">
                <div className="text-center max-w-xl">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/15 border border-emerald-500/40 grid place-items-center">
                    <Calculator size={28} className="text-emerald-400" />
                  </div>
                  <div className="font-display text-2xl font-bold mt-5">Sono il tuo Commercialista AI</div>
                  <p className="text-sm text-white/45 mt-2">
                    Ho accesso ai tuoi dati live (fatture, spese, contratti, IVA) e conosco il fisco italiano.
                    Posso aiutarti con liquidazioni IVA, scadenze, deducibilità costi noleggio (art. 164 TUIR),
                    pianificazione tributaria e crescita aziendale.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    {SUGGESTIONS.map((s) => {
                      const Icon = s.icon;
                      return (
                        <button key={s.txt} onClick={() => send(s.txt)}
                          className="text-xs px-3 py-2 rounded-full bg-white/[0.03] border border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition inline-flex items-center gap-2"
                          data-testid={`comm-sugg-${s.txt.slice(0, 15)}`}>
                          <Icon size={12} className="text-emerald-400" /> {s.txt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/40 grid place-items-center">
                    <Calculator size={14} className="text-emerald-400" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-emerald-500/15 border border-emerald-500/40 text-white" : "bg-white/[0.03] border border-white/10"
                }`}>
                  {m.content}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 shrink-0 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center">
                    <UserIcon size={14} className="text-white/70" />
                  </div>
                )}
              </motion.div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/40 grid place-items-center">
                  <Calculator size={14} className="text-emerald-400" />
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3 flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                      style={{ animation: `pulse-neon 1s ${i * 0.15}s ease-in-out infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/5 p-4 flex gap-3">
            <input data-testid="comm-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Chiedi al tuo commercialista…"
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-5 py-3 text-sm focus:border-emerald-500/60 focus:outline-none" />
            <button data-testid="comm-send" disabled={loading || !input.trim()} className="px-5 rounded-full inline-flex items-center gap-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-black font-bold disabled:opacity-50">
              <Send size={14} /> Invia
            </button>
          </form>
        </div>

        {/* Sidebar dati live */}
        <div className="glass rounded-2xl p-4 overflow-auto hidden lg:flex flex-col">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 font-display">Contesto live aziendale</div>
          <pre className="text-[11px] text-white/60 font-mono whitespace-pre-wrap leading-relaxed">{ctxPreview || "Caricamento..."}</pre>
          <div className="mt-3 text-[10px] text-white/30 leading-relaxed">
            Il commercialista AI vede questi dati in tempo reale e adatta i consigli al tuo stato attuale.
          </div>
        </div>
      </div>
    </div>
  );
}
